/**
 * CALL-CENTER-AGENT-SCOPE-AND-DISPOSITION-CORE-V1 -- the compact
 * disposition panel: the same-page contact-result + next-action loop
 * (Missions 2/3). Renders the dependency-gated chain
 * (method -> result -> connect -> interested detail -> next action +
 * callback/note) from the config-backed vocabulary; Save stays DISABLED
 * until the whole chosen path is valid; an upstream change clears stale
 * downstream selections. The SERVER re-validates every save -- this is a
 * usable in-place surface, never the security/validity boundary.
 *
 * Testability (repo idiom -- react-dom/server, no jsdom): the pure model
 * (lead-disposition-view.ts) and the presentational `DispositionPanelView`
 * are the pinned surfaces; the default container wires state + the
 * injected `save` fn.
 *
 * HONESTY: manual results are visibly labeled "Agent-recorded" (never
 * presented as provider-verified). Save feedback is honest
 * (success / error). No WhatsApp send, no telephony action.
 */

import { useState, type KeyboardEvent } from 'react';

import { PrimaryAction, AcInput, AcSelect } from '@/dashboard/ui';
import {
  buildDispositionPanelModel,
  dispositionSaveBody,
  EMPTY_DISPOSITION_SELECTION,
  selectConnect,
  selectDetail,
  selectMethod,
  selectNextAction,
  selectResult,
  type DispositionPanelModel,
  type DispositionSelection,
  type DispositionVocabulary,
} from '@/dashboard/leads/lead-disposition-view';

export const AGENT_RECORDED_BADGE = 'Agent-recorded';
export const DISPOSITION_SAVE_LABEL = 'Save contact result';
export const DISPOSITION_SAVED_COPY = 'Contact result saved.';
export const DISPOSITION_SAVE_FAILED_COPY = 'Could not save. Try again.';
export const METHOD_PLACEHOLDER = 'How did you make contact?';
export const RESULT_PLACEHOLDER = 'What happened?';
export const CONNECT_PLACEHOLDER = 'What did they say?';
export const DETAIL_PLACEHOLDER = 'More detail (optional)';
export const NEXT_ACTION_PLACEHOLDER = 'What happens next?';

export type DispositionSaveState =
  | { kind: 'idle' }
  | { kind: 'saving' }
  | { kind: 'saved' }
  | { kind: 'error' };

/**
 * X2 (ENTERPRISE-CRM-EXPERIENCE-ELEVATION-V1, SAFE tier): Enter submits the
 * disposition ONLY from a text/date INPUT (selects keep their native Enter
 * semantics; buttons submit themselves) and ONLY while the chain is valid
 * and not mid-save -- the Save-disabled law holds under the keyboard
 * exactly as it does under the pointer. Pure; pinned by unit test.
 */
export function dispositionEnterSubmits(
  targetTagName: string,
  canSave: boolean,
  saveStateKind: DispositionSaveState['kind'],
): boolean {
  return targetTagName === 'INPUT' && canSave && saveStateKind !== 'saving';
}

interface SelectRowProps {
  readonly testId: string;
  readonly label: string;
  readonly placeholder: string;
  readonly value: string;
  readonly options: ReadonlyArray<{ readonly id: string; readonly label: string }>;
  readonly onChange?: (value: string) => void;
}

function SelectRow({
  testId,
  label,
  placeholder,
  value,
  options,
  onChange,
}: SelectRowProps): JSX.Element {
  return (
    <label className="lead-disposition__row">
      <span className="lead-disposition__label">{label}</span>
      <AcSelect
        data-testid={testId}
        value={value}
        onChange={(e) => onChange?.(e.target.value)}
      >
        <option value="" disabled>
          {placeholder}
        </option>
        {options.map((o) => (
          <option key={o.id} value={o.id}>
            {o.label}
          </option>
        ))}
      </AcSelect>
    </label>
  );
}

export interface DispositionPanelViewProps {
  readonly vocabulary: DispositionVocabulary;
  readonly selection: DispositionSelection;
  readonly model: DispositionPanelModel;
  readonly saveState: DispositionSaveState;
  /** LEAD-CONTACT-FLOW-V1: the panel's question line. Default stays the
   *  original 'Record contact result' (every existing mount
   *  byte-identical); the NEXT-card contact workflow's advance step asks
   *  the founder-worded 'How did the contact go?' through this prop --
   *  ONE panel, ONE writer, only the heading differs (DENSITY: the
   *  question renders once, here, never as a second stacked heading). */
  readonly heading?: string;
  readonly onSelectMethod?: (v: string) => void;
  readonly onSelectResult?: (v: string) => void;
  readonly onSelectConnect?: (v: string) => void;
  readonly onSelectDetail?: (v: string) => void;
  readonly onSelectNextAction?: (v: string) => void;
  readonly onCallbackChange?: (isoLocal: string) => void;
  readonly onNoteChange?: (note: string) => void;
  readonly onSave?: () => void;
}

/** The pure presentational panel -- renders entirely from props. */
export function DispositionPanelView({
  vocabulary,
  selection,
  model,
  saveState,
  heading = 'Record contact result',
  onSelectMethod,
  onSelectResult,
  onSelectConnect,
  onSelectDetail,
  onSelectNextAction,
  onCallbackChange,
  onNoteChange,
  onSave,
}: DispositionPanelViewProps): JSX.Element {
  const onPanelKeyDown = (e: KeyboardEvent<HTMLElement>): void => {
    if (
      e.key === 'Enter' &&
      dispositionEnterSubmits(
        (e.target as HTMLElement).tagName,
        model.canSave,
        saveState.kind,
      )
    ) {
      e.preventDefault();
      onSave?.();
    }
  };
  return (
    <section
      className="lead-disposition"
      aria-label={heading}
      data-testid="lead-disposition-panel"
      onKeyDown={onPanelKeyDown}
    >
      <h4 className="lead360-section__title">
        {heading}{' '}
        <span className="ac-tag" data-testid="lead-disposition-agent-recorded">
          {AGENT_RECORDED_BADGE}
        </span>
      </h4>

      <SelectRow
        testId="lead-disposition-method"
        label="Method"
        placeholder={METHOD_PLACEHOLDER}
        value={selection.method ?? ''}
        options={vocabulary.methods}
        onChange={onSelectMethod}
      />

      {model.method !== null ? (
        <SelectRow
          testId="lead-disposition-result"
          label="Result"
          placeholder={RESULT_PLACEHOLDER}
          value={selection.resultId ?? ''}
          options={model.results}
          onChange={onSelectResult}
        />
      ) : null}

      {model.showConnect ? (
        <SelectRow
          testId="lead-disposition-connect"
          label="They said"
          placeholder={CONNECT_PLACEHOLDER}
          value={selection.connectId ?? ''}
          options={vocabulary.connectResults}
          onChange={onSelectConnect}
        />
      ) : null}

      {model.showDetail ? (
        <SelectRow
          testId="lead-disposition-detail"
          label="Detail"
          placeholder={DETAIL_PLACEHOLDER}
          value={selection.detailId ?? ''}
          options={vocabulary.interestedDetails}
          onChange={onSelectDetail}
        />
      ) : null}

      {model.result !== null ? (
        <SelectRow
          testId="lead-disposition-next-action"
          label="Next action"
          placeholder={NEXT_ACTION_PLACEHOLDER}
          value={selection.nextActionId ?? ''}
          /* LEAD-WORKFLOW-TRUTH-CORRECTIVE-V1 (M1-M3): offer ONLY the next
             actions lawful for the current cell (the server re-validates). */
          options={model.nextActions}
          onChange={onSelectNextAction}
        />
      ) : null}

      {model.showCallback ? (
        <label className="lead-disposition__row">
          <span className="lead-disposition__label">Callback date &amp; time</span>
          <AcInput
            type="datetime-local"
            data-testid="lead-disposition-callback"
            value={selection.callbackAtIso ?? ''}
            onChange={(e) => onCallbackChange?.(e.target.value)}
          />
        </label>
      ) : null}

      {model.showNote ? (
        <label className="lead-disposition__row">
          <span className="lead-disposition__label">Note</span>
          <AcInput
            type="text"
            data-testid="lead-disposition-note"
            value={selection.note}
            maxLength={200}
            onChange={(e) => onNoteChange?.(e.target.value)}
          />
        </label>
      ) : null}

      <div className="lead-disposition__actions">
        <PrimaryAction
          size="sm"
          data-testid="lead-disposition-save"
          disabled={!model.canSave || saveState.kind === 'saving'}
          onClick={onSave}
        >
          {saveState.kind === 'saving' ? 'Saving...' : DISPOSITION_SAVE_LABEL}
        </PrimaryAction>
        {saveState.kind === 'saved' ? (
          <span
            className="lead-disposition__feedback--ok"
            data-testid="lead-disposition-saved"
          >
            {DISPOSITION_SAVED_COPY}
          </span>
        ) : null}
        {saveState.kind === 'error' ? (
          <span
            className="lead-disposition__feedback--error"
            data-testid="lead-disposition-error"
          >
            {DISPOSITION_SAVE_FAILED_COPY}
          </span>
        ) : null}
      </div>
    </section>
  );
}

/**
 * What the completed save recorded -- handed to the host AFTER the save
 * succeeded (LEAD-TRUTH-AND-DEPENDENT-STATE-CORRECTIVE-V1, RA-2: the
 * disposition save and any follow-on flow stay two atomic acts; the host
 * may use `nextActionId` to open the EXISTING next surface, never to
 * merge the acts into one submit).
 */
export interface DispositionSavedInfo {
  readonly nextActionId: string | null;
}

export interface LeadDispositionPanelProps {
  readonly vocabulary: DispositionVocabulary;
  /** Performs the coherent save (POST /api/leads/:id/disposition via
   *  `saveLeadDisposition` in @/dashboard/api). Injected for tests. */
  readonly save: (body: ReturnType<typeof dispositionSaveBody>) => Promise<unknown>;
  /** Hosting list/record refetch after a successful save (My Work re-sort,
   *  timeline refresh). Receives the saved chain's next-action token so the
   *  record can chain into the existing booking flow (zero-arg callers keep
   *  working unchanged). */
  readonly onSaved?: (saved: DispositionSavedInfo) => void;
  /** See DispositionPanelViewProps.heading (LEAD-CONTACT-FLOW-V1). */
  readonly heading?: string;
}

/** The default container: selection state + upstream-clears-downstream +
 *  the injected save fn. */
export default function LeadDispositionPanel({
  vocabulary,
  save,
  onSaved,
  heading,
}: LeadDispositionPanelProps): JSX.Element {
  const [selection, setSelection] = useState<DispositionSelection>(
    EMPTY_DISPOSITION_SELECTION,
  );
  const [saveState, setSaveState] = useState<DispositionSaveState>({ kind: 'idle' });
  const model = buildDispositionPanelModel(vocabulary, selection);

  const onSave = (): void => {
    if (!model.canSave) return;
    setSaveState({ kind: 'saving' });
    // Capture the VALIDATED body before the post-save reset wipes the
    // selection -- its nextActionId is the saved chain's token (the server
    // re-validated the same body; on failure onSaved never fires).
    const body = dispositionSaveBody(selection);
    save(body)
      .then(() => {
        setSaveState({ kind: 'saved' });
        setSelection(EMPTY_DISPOSITION_SELECTION);
        onSaved?.({ nextActionId: body.nextActionId ?? null });
      })
      .catch(() => {
        // Honest, recoverable error state -- the selection is preserved so
        // the operator can retry without re-entering the chain.
        setSaveState({ kind: 'error' });
      });
  };

  return (
    <DispositionPanelView
      vocabulary={vocabulary}
      selection={selection}
      model={model}
      saveState={saveState}
      {...(heading !== undefined ? { heading } : {})}
      onSelectMethod={(v) => {
        setSaveState({ kind: 'idle' });
        setSelection((prev) => selectMethod(prev, v));
      }}
      onSelectResult={(v) => setSelection((prev) => selectResult(prev, v))}
      onSelectConnect={(v) => setSelection((prev) => selectConnect(prev, v))}
      onSelectDetail={(v) => setSelection((prev) => selectDetail(prev, v))}
      onSelectNextAction={(v) => setSelection((prev) => selectNextAction(prev, v))}
      onCallbackChange={(iso) =>
        setSelection((prev) => ({ ...prev, callbackAtIso: iso }))
      }
      onNoteChange={(note) => setSelection((prev) => ({ ...prev, note }))}
      onSave={onSave}
    />
  );
}
